import { BookOpen, ChartColumn, HandCoins, Landmark, LayoutDashboard, Percent, Settings, Users, Wallet } from "lucide-react";
import type { ComponentType } from "react";

export interface DomainItem { to: string; label: string; secondary?: boolean; hidden?: boolean; }
export interface AccountingDomain { id: string; label: string; to: string; icon: ComponentType<{className?:string}>; items: DomainItem[]; }
const item=(to:string,label:string,secondary=false):DomainItem=>({to,label,secondary});
const owned=(to:string,label:string):DomainItem=>({to,label,hidden:true});

export const ACCOUNTING_DOMAINS: AccountingDomain[] = [
  {id:'overview',label:'Overview',to:'/',icon:LayoutDashboard,items:[item('/','Overview')]},
  {id:'money-in',label:'Money in',to:'/invoices',icon:HandCoins,items:[
    item('/invoices','Invoices'),item('/quotes','Quotes'),item('/payments?tab=received','Payments received'),item('/reminders','Payment reminders'),item('/prepayments','Prepayments',true),item('/revenue-schedules','Revenue schedules',true),
    owned('/recurring-invoices','Recurring invoices'),
    owned('/reminders?section=rules','Reminder rules'),
    owned('/receipts','Receipts'),
  ]},
  {id:'money-out',label:'Money out',to:'/bills',icon:Wallet,items:[
    item('/bills','Bills'),item('/payments?tab=to-pay','Payments'),item('/expenses','Expenses'),item('/stock-costs','Landed costs',true),item('/accruals','Accruals',true),
    owned('/supplier-credits','Supplier credits'),owned('/recurring-bills','Recurring bills'),
    owned('/banking/batches','Payment runs'),owned('/banking/scheduled','Scheduled payments'),
  ]},
  {id:'contacts',label:'Contacts',to:'/contacts',icon:Users,items:[item('/contacts','Contacts')]},
  {id:'banking',label:'Banking',to:'/banking',icon:Landmark,items:[item('/banking','Bank accounts'),item('/banking/rules','Bank rules')]},
  {id:'ledger',label:'Ledger',to:'/journal',icon:BookOpen,items:[
    item('/journal','General ledger'),item('/chart-of-accounts','Chart of accounts'),item('/opening-balances','Opening balances'),
    item('/assets-and-loans','Assets & loans'),item('/source-journals','Source journals'),
    owned('/recurring-journals','Repeating entries'),owned('/fixed-assets','Fixed assets'),owned('/loans','Loans'),
    owned('/currencies','Currencies & rates'),
  ]},
  {id:'tax',label:'Tax',to:'/tax',icon:Percent,items:[
    item('/tax','Tax return'),owned('/tax/estimates','Tax estimates'),
  ]},
  {id:'reports',label:'Reports',to:'/reports',icon:ChartColumn,items:[
    item('/reports','All reports'),
    owned('/profit-and-loss','Profit & loss'),owned('/balance-sheet','Balance sheet'),owned('/trial-balance','Trial balance'),
    owned('/cash-flow','Cash-flow statement'),owned('/gl-query','Ledger search'),
    owned('/aged-receivables','Who owes you'),owned('/aged-payables','What you owe'),
    owned('/reports/budget-vs-actual','Budget vs actual'),owned('/reports/pnl-comparison','Profit & loss comparison'),
    owned('/reports/executive-summary','Executive summary'),owned('/reports/analytic','Costs by team or site'),
    owned('/reports/job-profitability','Work profitability'),owned('/reports/contractor-payments','Contractor payments'),
    owned('/reports/cultivation-close','Cultivation close'),
    owned('/audit-log','Audit log'),
  ]},
];

/** App-level destination pinned below the bookkeeping sections, with no flyout. */
export const ACCOUNTING_APP_SETTINGS: AccountingDomain = {
  id: 'settings',
  label: 'Settings',
  to: '/settings',
  icon: Settings,
  items: [item('/settings', 'Settings')],
};

/** Explicit ownership wins over historical path prefixes. Details inherit the list's domain. */
export function domainForPath(path:string,search:Record<string,unknown>={}): AccountingDomain | undefined {
  if(path==='/reports/imports'||path==='/reports/tax-estimates') return undefined;
  if(path==='/payments') return ACCOUNTING_DOMAINS.find(d=>d.id===(search.tab==='received'?'money-in':'money-out'));
  if(path==='/contacts'||path.startsWith('/contacts/')) return ACCOUNTING_DOMAINS.find(d=>d.id==='contacts');
  if(path.startsWith('/credit-notes/')) return ACCOUNTING_DOMAINS.find(d=>d.id==='money-in');
  const matches=ACCOUNTING_DOMAINS.flatMap(domain=>domain.items.filter(i=>!['/settings'].includes(i.to.split('?')[0])).map(i=>({domain,path:i.to.split('?')[0]})))
    .filter(i=>path===i.path||(i.path!=='/'&&path.startsWith(`${i.path}/`))).sort((a,b)=>b.path.length-a.path.length);
  return matches[0]?.domain;
}
